import React, { useEffect, useState } from 'react';
import { IInvoicePaidPlan } from '../Interfaces/IInvoicePaidPlan';
import { IInvoiceFullObject } from '../Interfaces/IInvoice';
import {
  TableCell,
  TableRow,
  useTheme,
  alpha,
  IconButton,
  Dialog,
  DialogContent,
  Button,
  DialogActions,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { CustomeTextField } from '../generic/CustomeTextField';
import { CreditCardOff, CreditScore, Delete } from '@mui/icons-material';
import { IInvoicePaid, invoicePaidSchema } from '../Interfaces/IInvoicePaid';
import { InvoicePaidTable } from './InvoicePaidTable';

interface IProps {
  index: number;
  editInvoice: IInvoiceFullObject;
  setEditInvoice: Function;
  invPaidPlan: IInvoicePaidPlan;
  invPaidPlans: IInvoicePaidPlan[];
  setInvPaidPlans: Function;
}

export const InvoicePaidPlanRow: React.FC<IProps> = ({
  index,
  editInvoice,
  setEditInvoice,
  invPaidPlan,
  invPaidPlans,
  setInvPaidPlans,
}) => {
  const theme = useTheme();

  const [editInvPaidPlan, setEditInvPaidPlan] = useState(invPaidPlan);
  const [newPaids, setNewPaids] = useState<IInvoicePaid[] | null>(null);
  const [isPending, setIsPending] = useState(false);

  const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const handleRemoveMe = () => {
    setInvPaidPlans([
      ...invPaidPlans.filter(
        plan => plan.idInvoicePaidPlan !== editInvPaidPlan.idInvoicePaidPlan
      ),
    ]);
  };

  const handleOpenNewPaid = () => {
    let idInvoicePaid = -1;

    if (editInvoice.InvoicePaids.length > 0) {
      const minTmpId =
        Math.min(...editInvoice.InvoicePaids.map(x => x.idInvoicePaid)) - 1;
      if (minTmpId <= idInvoicePaid) {
        idInvoicePaid = minTmpId - 1;
      }
    }

    const newObject = invoicePaidSchema.parse({
      idInvoicePaid: idInvoicePaid,
      PaidValue: editInvPaidPlan.PaidValue,
    });
    newObject.idInvoice = editInvoice.idInvoice;

    setNewPaids([newObject]);
  };

  const handleMarkAsPaid = async () => {
    if (newPaids !== null) {
      setIsPending(true);

      let idInvoicePaid = -1;
      const addPaids: IInvoicePaid[] = [];

      if (editInvoice.InvoicePaids.length > 0) {
        const minTmpId =
          Math.min(...editInvoice.InvoicePaids.map(x => x.idInvoicePaid)) - 1;
        if (minTmpId <= idInvoicePaid) {
          idInvoicePaid = minTmpId - 1;
        }
      }

      newPaids?.map((paid, idx) => {
        addPaids.push({
          ...paid,
          idInvoicePaid: idInvoicePaid - idx,
        });
      });

      setEditInvoice({
        ...editInvoice,
        InvoicePaids: [...editInvoice.InvoicePaids, ...addPaids],
      } as IInvoiceFullObject);

      // Dirty Workaround -> dass State editInv ready ist
      await wait(3000);

      setEditInvPaidPlan({
        ...editInvPaidPlan,
        wasPaid: true,
      });

      setIsPending(false);
      setNewPaids(null);
    }
  };

  const isToday = (dateStr: string) => {
    const today = new Date();
    const paidDate = new Date(dateStr);
    return (
      paidDate.getFullYear() === today.getFullYear() &&
      paidDate.getMonth() === today.getMonth() &&
      paidDate.getDate() === today.getDate()
    );
  };

  useEffect(() => {
    setInvPaidPlans([
      ...invPaidPlans.map(plan =>
        plan.idInvoicePaidPlan === editInvPaidPlan.idInvoicePaidPlan
          ? editInvPaidPlan
          : plan
      ),
    ]);
  }, [editInvPaidPlan]);

  return (
    <>
      {newPaids !== null && (
        <Dialog open onClose={() => setNewPaids(null)} maxWidth='sm' fullWidth>
          <DialogContent>
            <>
              <Box sx={{ mb: 5 }}>
                <Typography variant='h6'>
                  Summe der Ratenzahlung:
                  <Box sx={{ float: 'right' }}>
                    {editInvPaidPlan.PaidValue.toLocaleString('de-DE', {
                      style: 'currency',
                      currency: 'EUR',
                    })}
                  </Box>
                </Typography>
              </Box>

              <InvoicePaidTable
                key={`addedInvoice`}
                editInvoice={{
                  ...editInvoice,
                  InvoicePaids: newPaids,
                }}
                setEditInvoice={(updatedEdit: IInvoiceFullObject) =>
                  setNewPaids(updatedEdit.InvoicePaids)
                }
                disableHelping
              >
                <TableCell colSpan={3}>
                  Differenz zur Ratenzahlung
                  <Typography sx={{ float: 'right', fontWeight: 'bold' }}>
                    {(
                      newPaids
                        .map(newPaid => newPaid.PaidValue)
                        .reduce((a, b) => a + b, 0) - editInvPaidPlan.PaidValue
                    ).toLocaleString('de-DE', {
                      style: 'currency',
                      currency: 'EUR',
                    })}
                  </Typography>
                </TableCell>
              </InvoicePaidTable>
            </>
          </DialogContent>
          <DialogActions>
            <Button variant='outlined'>Abbruch</Button>
            <Button
              variant='contained'
              onClick={handleMarkAsPaid}
              disabled={isPending}
            >
              {isPending ? (
                <Box display='flex' alignItems='center' gap={1}>
                  <span>Markieren</span>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                'Markieren'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <TableRow
        sx={{
          backgroundColor: editInvPaidPlan.wasPaid
            ? alpha(theme.palette.success.light, 0.1)
            : isToday(editInvPaidPlan.PaidDate)
              ? alpha(theme.palette.warning.light, 0.2)
              : new Date() > new Date(editInvPaidPlan.PaidDate)
                ? alpha(theme.palette.error.light, 0.2)
                : undefined,
        }}
      >
        <TableCell>{index + 1}. Rate</TableCell>
        <TableCell>
          <CustomeTextField
            label='Frist'
            attr='PaidDate'
            object={editInvPaidPlan}
            setObject={setEditInvPaidPlan}
            type='date'
            required
            disabled={editInvPaidPlan.wasPaid}
          />
        </TableCell>
        <TableCell>
          <CustomeTextField
            label='Summe'
            attr='PaidValue'
            object={editInvPaidPlan}
            setObject={setEditInvPaidPlan}
            type='euro'
            required
            disabled={editInvPaidPlan.wasPaid}
          />
        </TableCell>
        <TableCell>
          {editInvPaidPlan.wasPaid ? (
            <IconButton
              title='Als ungezahlt markieren.'
              onClick={() =>
                setEditInvPaidPlan({
                  ...editInvPaidPlan,
                  wasPaid: false,
                })
              }
              size='small'
            >
              <CreditCardOff />
            </IconButton>
          ) : (
            <IconButton
              title='Als gezahlt markieren.'
              onClick={handleOpenNewPaid}
              size='small'
            >
              <CreditScore />
            </IconButton>
          )}
          <IconButton
            onClick={handleRemoveMe}
            size='small'
            disabled={editInvPaidPlan.wasPaid}
          >
            <Delete />
          </IconButton>
        </TableCell>
      </TableRow>
    </>
  );
};
